/**
 * Trigger button for MultiTagFilterDropdown — mirrors the single-select
 * trigger, and wears the tag's own color when exactly one is picked.
 * For Media Devoted | Created By Ciro Autiero
 */

import { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import { getTagOptionStyle, NO_TAGS_VALUE } from '../TagFilterDropdown';

/** Trigger text: the tag itself when one is picked, a count beyond that. */
export function multiTagLabel(
  values: string[],
  placeholder: string,
  noneLabel: string,
): string {
  if (values.length === 0) return placeholder;
  if (values.length === 1) return values[0] === NO_TAGS_VALUE ? noneLabel : values[0]!;
  return `${values.length} selected`;
}

export interface MultiTagTriggerProps {
  values: string[];
  tagColors?: Record<string, string>;
  placeholder: string;
  noneLabel: string;
  compact: boolean;
  open: boolean;
}

export const MultiTagTrigger = forwardRef<HTMLButtonElement, MultiTagTriggerProps>(
  ({ values, tagColors, placeholder, noneLabel, compact, open, ...rest }, ref) => {
    const single = values.length === 1 && values[0] !== NO_TAGS_VALUE ? values[0]! : '';
    const style = single ? getTagOptionStyle(single, tagColors, true) : undefined;
    const border = style && tagColors?.[single] ? { borderColor: `${tagColors[single]}60` } : undefined;

    return (
      <button
        {...rest}
        ref={ref}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Filter by tag — ${values.length} selected`}
        className={cn(
          'flex min-w-[72px] shrink items-center justify-between gap-2 rounded border border-input px-2.5 text-left text-sm font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          !style && 'text-foreground',
          !style && values.length > 0 && 'border-primary/60 bg-primary/10',
          !style && values.length === 0 && 'bg-background hover:bg-accent/50',
          compact ? 'h-8' : 'h-9',
        )}
        style={style ? { ...style, ...border } : undefined}
      >
        <span className="min-w-0 max-w-[100px] truncate">
          {multiTagLabel(values, placeholder, noneLabel)}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 opacity-80',
            open && 'rotate-180',
            style ? '' : 'text-muted-foreground',
          )}
          style={style ? { color: style.color } : undefined}
        />
      </button>
    );
  },
);
MultiTagTrigger.displayName = 'MultiTagTrigger';
